// Terms.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Authentication.css';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div
      className="liwaza-layout"
      style={{ padding: '40px 24px', textAlign: 'left' }}
    >
      <h1
        className="liwaza-title"
        style={{ fontSize: '1.75rem', marginBottom: '24px' }}
      >
        Terms of Use and Privacy Policy
      </h1>
      <p className="liwaza-subtitle" style={{ marginBottom: '32px' }}>
        Last updated: June 16, 2025
      </p>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        1. General Information
      </h3>
      <p>
        The Liwaza platform is operated by Chinery SAS, a company registered in
        France under SIREN 938 593 290, with its registered office at 60 RUE
        FRANCOIS IER, 75008 PARIS.
      </p>
      <p>
        For any questions regarding this policy or your personal data
        (downloading and erasing), please contact us at{' '}
        <a href="mailto:contact@liwaza.org">contact@liwaza.org</a>.
      </p>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        2. Data Collection
      </h3>
      <p>
        Liwaza collects certain personal information when you use our services,
        including but not limited to:
      </p>
      <ul>
        <li>First and last name</li>
        <li>Email address</li>
        <li>Payment information (via Stripe)</li>
        <li>Navigation data (via Google Analytics and Microsoft Clarity)</li>
        <li>Academic profile information and application materials</li>
        <li>Test scores and preparation progress</li>
      </ul>
      <p>
        This data is stored during the duration of your subscription (and 6
        months after the end of your subscription), unless otherwise indicated
        by you or required for service delivery.
      </p>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        3. Services Offered
      </h3>
      <p>
        Liwaza offers several services to graduate school candidates, including
        but not limited to:
      </p>
      <ul>
        <li>
          AI-powered coaching for GRE, TAGE MAGE, IELTS/TOEIC and interview
          preparation
        </li>
        <li>
          Expert profile building for top 100 business schools (FT ranking)
        </li>
        <li>Mock interview sessions for graduate school admissions</li>
        <li>Resume optimization tools with ATS compatibility</li>
        <li>Essay optimization and feedback</li>
        <li>Community forum access and networking</li>
        <li>
          One-on-one coaching with graduate school alumni and current students
        </li>
        <li>Application tracking and deadline management</li>
      </ul>
      <p>
        Some services are free, while others require payment through our premium
        subscription via Stripe.
      </p>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        4. Subscription and Payment Policy
      </h3>
      <p>
        <strong>Subscription Cancellation:</strong> You can cancel your
        subscription at any time. You'll continue to have access until the end
        of your current billing period.
      </p>

      <p>
        <strong>Refund Policy:</strong> Liwaza AI provides a comprehensive
        solution to help you master all the fundamentals needed to prepare a
        strong application profile for top graduate schools worldwide, starting
        with graduate school programs. The success of our platform depends on
        your dedication to using the solution consistently (AI coach + community
        + coaches) to improve your GRE/TAGE MAGE scores, IELTS/TOEIC scores, CV,
        essays, and interview performance.
      </p>

      <p>
        For this reason, we do not provide a money-back guarantee. However, we
        put everything in place for you to succeed, including:
      </p>
      <ul>
        <li>Comprehensive AI coaching and personalized learning plans</li>
        <li>
          Access to expert coaches who are alumni and current students from top
          business schools
        </li>
        <li>Community support and networking opportunities</li>
        <li>Continuous platform improvements based on user feedback</li>
      </ul>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        5. Success Methodology
      </h3>
      <p>
        Success with Liwaza (as demonstrated by our success stories) comes from
        using the solution consistently throughout your preparation period. To
        maximize your chances of admission:
      </p>
      <ul>
        <li>Use the platform consistently and regularly</li>
        <li>Follow the personalized learning plan generated for you</li>
        <li>Engage actively with our AI coach and human coaches</li>
        <li>Participate in the community discussions</li>
        <li>Trust the process and remain committed to your goals</li>
      </ul>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        6. Our Coaching Community
      </h3>
      <p>
        Liwaza is powered by both AI technology and a community of current
        graduate school students and alumni who work together to help
        underserved candidates access top graduate schools worldwide. Our
        coaches are:
      </p>
      <ul>
        <li>Current students and alumni from top-ranked business schools</li>
        <li>
          Experienced professionals who have successfully navigated the
          admission process
        </li>
        <li>
          Experts who provide final feedback on profiles, CVs, essays, and
          program-specific questions
        </li>
        <li>
          Mentors who complement AI insights with human expertise and real-world
          experience
        </li>
      </ul>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        7. User Accounts and Content
      </h3>
      <p>
        Users can create an account and publish content on the platform. You can
        request account deletion by sending an email to{' '}
        <a href="mailto:contact@liwaza.org">contact@liwaza.org</a>. All
        user-generated content is subject to moderation to ensure a respectful
        and secure environment.
      </p>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        8. Data Sharing and Privacy
      </h3>
      <p>
        We do not share users' personal data with third parties without consent.
        However, we may use aggregated data for research and analysis purposes.
        We integrate with external tools like Google Workspace, Zoom, Calendly,
        and Stripe for enhanced functionality.
      </p>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        9. Expected Outcomes
      </h3>
      <p>Our AI coaching is designed to help you achieve:</p>
      <ul>
        <li>322+ score in GRE</li>
        <li>Band 7+ in IELTS</li>
        <li>300+ score in TAGE MAGE</li>
        <li>
          Optimized profiles for top 100 business schools (Financial Times
          ranking)
        </li>
        <li>Enhanced interview performance and application materials</li>
      </ul>

      <h3
        className="liwaza-title"
        style={{ fontSize: '1.2rem', marginTop: '24px' }}
      >
        10. GDPR Compliance
      </h3>
      <p>
        In accordance with GDPR, you can request deletion of your data or
        exercise your right of access by contacting us at{' '}
        <a href="mailto:contact@liwaza.org">contact@liwaza.org</a>. We are
        committed to protecting your privacy and ensuring transparent data
        practices.
      </p>

      <div style={{ marginTop: '40px', textAlign: 'left' }}>
        <button
          className="btn-lwz-secondary"
          onClick={() => navigate('/login')}
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default Terms;
